import { useCallback, useEffect, useRef } from 'react';

type AutosaveOptions<T> = {
  enabled?: boolean;
  delayMs?: number;
  serialize?: (value: T) => string;
};

/**
 * Düzenleme sırasında debounced kayıt + unmount/çıkışta flush.
 * İlk açılıştaki değeri kaydetmez; yalnızca değişiklikten sonra persist eder.
 */
export function useEditorAutosave<T>(
  value: T,
  persist: (next: T) => Promise<void> | void,
  { enabled = true, delayMs = 700, serialize = defaultSerialize }: AutosaveOptions<T> = {}
): { flush: () => Promise<void> } {
  const persistRef = useRef(persist);
  persistRef.current = persist;
  const valueRef = useRef(value);
  valueRef.current = value;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const serializeRef = useRef(serialize);
  serializeRef.current = serialize;
  const savedRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef<Promise<void> | null>(null);

  const snapshot = serialize(value);

  const flush = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (!enabledRef.current) return;
    const current = serializeRef.current(valueRef.current);
    if (savedRef.current === null || savedRef.current === current) return;

    const pending = Promise.resolve(persistRef.current(valueRef.current))
      .then(() => {
        savedRef.current = serializeRef.current(valueRef.current);
      })
      .catch((error) => {
        console.error('[EDITOR AUTOSAVE] Kayıt hatası:', error);
        throw error;
      });

    inFlightRef.current = pending;
    try {
      await pending;
    } catch {
      // Persist hatası çağıran tarafta / console'da görünür; flush yutmaz.
    } finally {
      if (inFlightRef.current === pending) inFlightRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      savedRef.current = null;
      return;
    }
    if (savedRef.current === null) {
      savedRef.current = snapshot;
    }
  }, [enabled, snapshot]);

  useEffect(() => {
    if (!enabled) return;
    if (savedRef.current === null || savedRef.current === snapshot) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      void flush();
    }, delayMs);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [enabled, snapshot, delayMs, flush]);

  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (!enabledRef.current) return;
      const current = serializeRef.current(valueRef.current);
      if (savedRef.current === null || savedRef.current === current) return;
      void persistRef.current(valueRef.current);
    },
    []
  );

  return { flush };
}

function defaultSerialize<T>(value: T): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
